<template>
	<div class="cadastre-filters">
		<section class="filters">
			<div class="content range-filters">
				<div class="range superficy">
					<div class="values">
						<AreaRangeNotchedSlider
							v-model="superficyRange"
							:label="t('opportunities.unitFields.superficy')"
							:min="0"
							:max="RANGE_SLIDER_MAX"
							:thresholds="[10000, 50000, 250000]"
							@change="analytics.rangeFilterChanged('opportunities.superficy', $event)"
						/>
					</div>
				</div>

				<template v-if="canAccessField(store.currentUser, FieldAccess.Sensitive)">
					<div v-for="filter in rangeFilters" :key="filter.titleKey" class="range">
						<div class="values">
							<RangeNotchedSlider
								:model-value="filter.value"
								:label="t(filter.titleKey)"
								:min="0"
								:max="RANGE_SLIDER_MAX"
								:thresholds="filter.thresholds"
								:open-ended-anchor="filter.openEndedAnchor"
								:unit-prefix="filter.unitPrefix"
								@update:model-value="range => updateRange(filter, range)"
								@change="analytics.rangeFilterChanged(filter.titleKey, $event)"
							/>
						</div>
					</div>
				</template>
			</div>

			<div v-if="visibleBooleanFilters.length > 0" class="boolean-filters">
				<h5>{{ t("filters.other-filters-title") }}</h5>
				<div v-for="filter in visibleBooleanFilters" :key="filter.field" class="boolean-filter">
					<span class="label">{{ t(filter.labelKey) }}</span>
					<div class="options" role="group">
						<button
							v-for="option in booleanFilterOptions"
							:key="String(option.value)"
							type="button"
							class="option"
							:class="{ active: filter.value === option.value }"
							@click="setBooleanFilter(filter.field, option.value)"
						>
							{{ t(option.labelKey) }}
						</button>
					</div>
				</div>
			</div>

			<div v-if="store.isUserLoggedIn" class="toggle">
				<span class="label">{{ t("filters.show-streets") }}</span>
				<ArkBaseSwitch :checked="showStreets" @change="onToggleStreets" />
			</div>
		</section>
	</div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"

import ArkBaseSwitch from "@/components/ui/ArkBaseSwitch.vue"
import AreaRangeNotchedSlider from "@/components/ui/AreaRangeNotchedSlider.vue"
import RangeNotchedSlider from "@/components/ui/RangeNotchedSlider.vue"

import { RANGE_SLIDER_MAX } from "@/components/constants"
import { useNavigationContext } from "@/composables/useNavigationContext"
import { useView } from "@/composables/useView"
import { Permission, UseRootStore } from "@/model/RootStore"
import { ProjectStoreKey } from "@/stores/projectStore"
import { ParcelBooleanFilterField, ViewScope } from "@/types"
import { areEqualIgnoringOrder } from "@/utils"
import { FieldAccess, canAccessField, type FieldVisibility } from "@/utils/fieldAccess"
import { analytics } from "@/utils/analytics"

const emit = defineEmits<{ "user-change": [] }>()

const { t } = useI18n()
const store = UseRootStore()
const view = useView(ViewScope.ParcelsMap)
const huntingView = useView(ViewScope.ParcelsHuntingMap)
const projectStore = inject(ProjectStoreKey)!()
const { currentProject } = storeToRefs(projectStore)
const { isMapMode, isHuntingSection } = useNavigationContext({ currentProject })
const activeView = computed(() => (isMapMode.value && isHuntingSection.value ? huntingView : view))

const showStreets = computed(() => activeView.value.view.value.filter?.parcel?.showStreets ?? false)

function onToggleStreets(value: boolean): void {
	activeView.value.setParcelShowStreets(value)
	emit("user-change")
}

interface ParcelBooleanFilter {
	field: ParcelBooleanFilterField
	labelKey: string
	value: boolean | null
	visibility: FieldVisibility
}

const booleanFilterOptions: { value: boolean | null; labelKey: string }[] = [
	{ value: null, labelKey: "filters.boolean-filter-any" },
	{ value: true, labelKey: "filters.boolean-filter-yes" },
	{ value: false, labelKey: "filters.boolean-filter-no" },
]

const booleanFilters = computed<ParcelBooleanFilter[]>(() => {
	const parcelFilter = activeView.value.view.value.filter?.parcel
	return [
		{
			field: ParcelBooleanFilterField.PpuArea,
			labelKey: "parcelCard.badges.ppu",
			value: parcelFilter?.ppuArea ?? null,
			visibility: Permission.PlanningPolicyRead,
		},
		{
			field: ParcelBooleanFilterField.TodArea,
			labelKey: "parcelCard.badges.tod",
			value: parcelFilter?.todArea ?? null,
			visibility: Permission.PlanningPolicyRead,
		},
		{
			field: ParcelBooleanFilterField.SoilContaminated,
			labelKey: "parcelCard.badges.soilContaminated",
			value: parcelFilter?.soilContaminated ?? null,
			visibility: FieldAccess.Authenticated,
		},
		{
			field: ParcelBooleanFilterField.Wetland,
			labelKey: "parcelCard.badges.wetland",
			value: parcelFilter?.wetland ?? null,
			visibility: Permission.WetlandsRead,
		},
		{
			field: ParcelBooleanFilterField.Rainette,
			labelKey: "parcelCard.badges.rainette",
			value: parcelFilter?.rainette ?? null,
			visibility: Permission.EndangeredSpeciesRead,
		},
	]
})

const visibleBooleanFilters = computed(() =>
	booleanFilters.value.filter(filter => canAccessField(store.currentUser, filter.visibility))
)

function setBooleanFilter(field: ParcelBooleanFilterField, value: boolean | null): void {
	activeView.value.setParcelBooleanFilter(field, value)
	emit("user-change")
}

let isApplyingViewFilter = false
const superficyRange = ref<[number, number]>([0, RANGE_SLIDER_MAX])

function rangeValue(range: { min?: number | null; max?: number | null } | undefined): [number, number] {
	return [range?.min ?? 0, range?.max ?? RANGE_SLIDER_MAX]
}

interface ParcelRangeFilter {
	titleKey: string
	thresholds: [number, number, number]
	openEndedAnchor?: number
	unitPrefix: string
	value: [number, number]
	setRange: (range: [number, number]) => void
}

const rangeFilters = computed<ParcelRangeFilter[]>(() => {
	const parcelFilter = activeView.value.view.value.filter?.parcel
	return [
		{
			titleKey: "parcelCard.dwellingUnits",
			thresholds: [2, 5, 10],
			unitPrefix: "",
			value: rangeValue(parcelFilter?.dwellingUnits),
			setRange: activeView.value.setParcelDwellingUnitsRange,
		},
		{
			titleKey: "parcelCard.storeys",
			thresholds: [2, 4, 8],
			unitPrefix: "",
			value: rangeValue(parcelFilter?.storeys),
			setRange: activeView.value.setParcelStoreysRange,
		},
		{
			titleKey: "filters.building-age",
			thresholds: [5, 20, 50],
			openEndedAnchor: 80,
			unitPrefix: "",
			value: rangeValue(parcelFilter?.buildingAge),
			setRange: activeView.value.setParcelBuildingAgeRange,
		},
		{
			titleKey: "filters.property-value",
			thresholds: [600_000, 1_000_000, 6_500_000],
			unitPrefix: "$",
			value: rangeValue(parcelFilter?.propertyValue),
			setRange: activeView.value.setParcelPropertyValueRange,
		},
	]
})

function updateRange(filter: ParcelRangeFilter, range: [number, number]): void {
	if (areEqualIgnoringOrder(range, filter.value)) return
	filter.setRange(range)
	emit("user-change")
}

watch(
	superficyRange,
	(range, oldRange) => {
		if (isApplyingViewFilter) return
		activeView.value.setParcelSuperficyRange(range)
		if (!areEqualIgnoringOrder(range, oldRange)) {
			emit("user-change")
		}
	},
	{ deep: true }
)

watch(
	() => [activeView.value.view.value.scope, activeView.value.view.value.filter?.parcel?.superficy] as const,
	([, superficy]) => {
		isApplyingViewFilter = true
		superficyRange.value = rangeValue(superficy)
		nextTick(() => {
			isApplyingViewFilter = false
		})
	},
	{ deep: true, immediate: true }
)

const resetFilters = () => {
	isApplyingViewFilter = true
	superficyRange.value = [0, RANGE_SLIDER_MAX]
	activeView.value.resetFilter()
	nextTick(() => {
		isApplyingViewFilter = false
	})
}

defineExpose({ resetFilters })
</script>

<style scoped>
.cadastre-filters {
	flex-grow: 1;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);

	.content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-md);
	}

	.toggle {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-sm);
		margin-top: var(--spacing-md);
		padding-top: var(--spacing-md);
		border-top: var(--border-width-thin) solid var(--neutral-200);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		color: var(--neutral-700);
	}

	.range-filters {
		padding: 0;

		.range {
			--input-width: var(--width-input-field);

			.title {
				font-family: var(--font-family-primary);
				font-size: var(--font-size-regular);
				font-weight: var(--font-weight-regular);
			}

			.values {
				margin-top: var(--spacing-sm);
			}
		}
	}

	.filters {
		min-height: var(--spacing-2xl);
		font-size: var(--font-size-small);
		width: 100%;
		position: relative;
	}

	.boolean-filters {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		padding-top: var(--spacing-md);

		h5 {
			margin: 0 0 var(--spacing-xs);
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;
		}

		.boolean-filter {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-sm);

			@media (max-width: 1510px) {
				flex-direction: column;
				align-items: stretch;
			}

			.label {
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				color: var(--neutral-700);
			}

			.options {
				display: flex;
				gap: var(--spacing-xs);
				padding: var(--spacing-xxs);
				background-color: var(--neutral-0);
				border: 1px solid var(--neutral-200);
				border-radius: var(--border-radius-full);

				@media (max-width: 1510px) {
					width: 100%;
				}

				.option {
					padding: var(--spacing-xs) var(--spacing-sm);
					border: none;
					border-radius: var(--border-radius-full);
					background: transparent;
					color: var(--neutral-600);
					font-family: var(--font-family-primary);
					font-size: var(--font-size-xs);
					font-weight: var(--font-weight-medium);
					white-space: nowrap;
					cursor: pointer;

					@media (max-width: 1510px) {
						flex: 1;
					}

					&:hover:not(.active) {
						color: var(--neutral-900);
					}

					&.active {
						background-color: var(--primary-green);
						color: var(--neutral-0);
					}
				}
			}
		}
	}
}
</style>
